import { Marker, Source, Layer } from "react-map-gl/maplibre";
import type { MarkerDragEvent } from "react-map-gl/maplibre";
import { bboxesFromLLUR } from "../lib/map/bbox";
import type { LngLatLike } from "../lib/units";

interface Props {
    ll: LngLatLike;
    ur: LngLatLike;
    width: number;
    height: number;
    interactive?: boolean;
    onBoundsChange?: (ll: LngLatLike, ur: LngLatLike) => void;
}

function sort(a: LngLatLike, b: LngLatLike): [LngLatLike, LngLatLike] {
    const minLng = Math.min(a.lng, b.lng);
    const maxLng = Math.max(a.lng, b.lng);
    const minLat = Math.min(a.lat, b.lat);
    const maxLat = Math.max(a.lat, b.lat);
    return [{ lng: minLng, lat: minLat }, { lng: maxLng, lat: maxLat }];
}

type Role = "ll" | "lr" | "ul" | "ur" | "t" | "b" | "l" | "r" | "mid";

function applyDrag(
    role: Role,
    dragged: LngLatLike,
    ll: LngLatLike,
    ur: LngLatLike,
): [LngLatLike, LngLatLike] {
    let newLL = { ...ll };
    let newUR = { ...ur };
    switch (role) {
        case "ll":
            newLL = { lng: dragged.lng, lat: dragged.lat };
            break;
        case "lr":
            newLL = { lng: ll.lng, lat: dragged.lat };
            newUR = { lng: dragged.lng, lat: ur.lat };
            break;
        case "ul":
            newLL = { lng: dragged.lng, lat: ll.lat };
            newUR = { lng: ur.lng, lat: dragged.lat };
            break;
        case "ur":
            newUR = { lng: dragged.lng, lat: dragged.lat };
            break;
        case "t":
            newUR = { lng: ur.lng, lat: dragged.lat };
            break;
        case "b":
            newLL = { lng: ll.lng, lat: dragged.lat };
            break;
        case "l":
            newLL = { lng: dragged.lng, lat: ll.lat };
            break;
        case "r":
            newUR = { lng: dragged.lng, lat: ur.lat };
            break;
        case "mid": {
            const midLng = (ll.lng + ur.lng) / 2;
            const midLat = (ll.lat + ur.lat) / 2;
            const dLng = dragged.lng - midLng;
            const dLat = dragged.lat - midLat;
            newLL = { lng: ll.lng + dLng, lat: ll.lat + dLat };
            newUR = { lng: ur.lng + dLng, lat: ur.lat + dLat };
            break;
        }
    }
    return sort(newLL, newUR);
}

const CORNER_COLOR = "#eba834";
const SIDE_COLOR = "#ebcc34";
const CENTER_COLOR = "#b4eb34";

function Handle({ color, interactive }: { color: string; interactive: boolean }) {
    return (
        <div
            style={{
                background: interactive ? color : "rgba(0,0,0,0)",
                width: "1.6em",
                height: "1.6em",
                borderRadius: "0.3em",
                cursor: interactive ? "grab" : "default",
            }}
        />
    );
}

export function BBoxSelector({
    ll,
    ur,
    width,
    height,
    interactive = true,
    onBoundsChange,
}: Props) {
    const bboxData = bboxesFromLLUR(ll, ur, width, height);

    const midLng = (ll.lng + ur.lng) / 2;
    const midLat = (ll.lat + ur.lat) / 2;

    const markers: Array<{ role: Role; lng: number; lat: number; color: string }> = [
        { role: "ll", lng: ll.lng, lat: ll.lat, color: CORNER_COLOR },
        { role: "lr", lng: ur.lng, lat: ll.lat, color: CORNER_COLOR },
        { role: "ul", lng: ll.lng, lat: ur.lat, color: CORNER_COLOR },
        { role: "ur", lng: ur.lng, lat: ur.lat, color: CORNER_COLOR },
        { role: "t", lng: midLng, lat: ur.lat, color: SIDE_COLOR },
        { role: "b", lng: midLng, lat: ll.lat, color: SIDE_COLOR },
        { role: "l", lng: ll.lng, lat: midLat, color: SIDE_COLOR },
        { role: "r", lng: ur.lng, lat: midLat, color: SIDE_COLOR },
        { role: "mid", lng: midLng, lat: midLat, color: CENTER_COLOR },
    ];

    const handleDragEnd = (role: Role) => (e: MarkerDragEvent) => {
        if (!onBoundsChange) return;
        const [newLL, newUR] = applyDrag(role, e.lngLat, ll, ur);
        onBoundsChange(newLL, newUR);
    };

    return (
        <>
            <Source id="bbox" type="geojson" data={bboxData}>
                <Layer
                    id="bbox-lines"
                    type="line"
                    layout={{ "line-cap": "round", "line-join": "round" }}
                    paint={{
                        "line-width": 3,
                        "line-dasharray": [3, 3],
                        "line-color": "#FFBF00",
                        "line-opacity": 0.8,
                    }}
                />
            </Source>
            {markers.map((m) => (
                <Marker
                    key={m.role}
                    longitude={m.lng}
                    latitude={m.lat}
                    draggable={interactive}
                    onDragEnd={handleDragEnd(m.role)}
                >
                    <Handle color={m.color} interactive={interactive} />
                </Marker>
            ))}
        </>
    );
}
